import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import i18n from '../../../i18n';
import {connect} from "react-redux";
import {Redirect} from "react-router-dom";
import httpService from "../../../services/http.service";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import AppAlert from "../../Shared/AppAlert";
import useAlert from "../../../hooks/useAlert";
import {extractResponseValidationErrors} from "../../../utils/index";

const emptyRole = {id: null, name: '', description: '', permissions: [], is_system: false};

const errorMessage = (e) => {
    const response = e?.response;
    if (response?.status === 422) {
        return extractResponseValidationErrors(response.data)[0];
    }
    return response?.data?.message || i18n.t('Action impossible, veuillez réessayer.');
};

/** Rôles et permissions par module (réservé aux comptes root). Les droits sont appliqués par l'API. */
const RoleList = ({authUser}) => {
    const {t} = useTranslation();
    const [fetching, setFetching] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [roles, setRoles] = useState([]);
    const [modules, setModules] = useState([]);
    const [role, setRole] = useState(null);
    const [alert, setAlert, onClose] = useAlert();

    const load = async () => {
        const [{data: rolesResponse}, {data: catalog}] = await Promise.all([
            httpService.get('/roles'),
            httpService.get('/roles/catalog'),
        ]);
        setRoles(rolesResponse.data || []);
        setModules(catalog.data || []);
        setFetching(false);
    };

    useEffect(() => {
        if (authUser?.isRoot) {
            load().catch(() => setFetching(false));
        }
    }, []);

    if (!authUser?.isRoot) {
        return <Redirect to="/unauthorize" />;
    }

    const groups = modules.reduce((acc, module) => {
        (acc[module.group] = acc[module.group] || []).push(module);
        return acc;
    }, {});

    const openModal = (value) => {
        setAlert(null);
        setRole({...value, permissions: [...(value.permissions || [])]});
    };

    const togglePermission = (key) => {
        setRole((state) => ({
            ...state,
            permissions: state.permissions.includes(key)
                ? state.permissions.filter(k => k !== key)
                : [...state.permissions, key],
        }));
    };

    const toggleGroup = (items) => {
        const keys = items.map(m => m.key);
        const all = keys.every(k => role.permissions.includes(k));
        setRole((state) => ({
            ...state,
            permissions: all
                ? state.permissions.filter(k => !keys.includes(k))
                : Array.from(new Set([...state.permissions, ...keys])),
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setAlert(null);
        try {
            const payload = {name: role.name, description: role.description || null, permissions: role.permissions};
            if (role.id) {
                await httpService.put(`/roles/${role.id}`, payload);
            } else {
                await httpService.post('/roles', payload);
            }
            await load();
            setRole(null);
        } catch (err) {
            setAlert({type: 'danger', message: errorMessage(err)});
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (item) => {
        if (!window.confirm(t("Supprimer le rôle « {{name}} » ?", {name: item.name}))) return;
        try {
            await httpService.delete(`/roles/${item.id}`);
            setRoles(roles.filter(r => r.id !== item.id));
        } catch (err) {
            window.alert(errorMessage(err));
        }
    };

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={t("Rôles et permissions")}>
                <div className="col-md-6 col-sm-12 text-right">
                    <button className="btn btn-sm btn-primary font-weight-bold" onClick={() => openModal(emptyRole)}>
                        <i className="fa fa-plus"></i>{' '}
                        <span>{t('Nouveau rôle')}</span>
                    </button>
                </div>
            </AppBreadcrumb>

            <p className="text-muted">{t("Les permissions s'appliquent par module. Le compte root a tous les droits ; la gestion des administrateurs et des rôles lui reste réservée. Modifier un rôle déconnecte ses titulaires, qui retrouvent leur menu à la prochaine connexion.")}</p>

            {fetching ? <FecthingSpinner /> : (
                <div className="table-responsive">
                    <table className="table table-hover table-custom spacing5">
                        <thead>
                        <tr>
                            <th>{t('Rôle')}</th>
                            <th>{t('Description')}</th>
                            <th>{t('Modules')}</th>
                            <th>{t('Comptes')}</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {roles.map(item => (
                            <tr key={item.id}>
                                <td>
                                    <span className="font-weight-bold">{item.name}</span>{' '}
                                    {item.is_system && <span className="badge badge-default">{t("Par défaut")}</span>}
                                </td>
                                <td>{item.description}</td>
                                <td>{item.permissions.length} / {modules.length}</td>
                                <td>{item.moderateurs_count}</td>
                                <td className="text-right">
                                    <button className="btn btn-primary btn-sm font-weight-bold mr-1" onClick={() => openModal(item)}>
                                        {t('Modifier')}
                                    </button>
                                    {!item.is_system && (
                                        <button className="btn btn-danger btn-sm font-weight-bold" onClick={() => onDelete(item)}>
                                            {t('Supprimer')}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className={`modal fade ${role ? 'd-block show' : ''}`} style={{overflowY: 'auto'}} onClick={() => null}>
                <div className="modal-dialog modal-lg" role="document">
                    {role && (
                        <form onSubmit={onSubmit}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{role.id ? t('Modifier le rôle') : t('Nouveau rôle')}</h5>
                                    <button type="button" className="close" aria-label="Close" onClick={() => setRole(null)}>
                                        <span aria-hidden="true">×</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    {alert && <AppAlert onClose={onClose} type={alert.type}>{alert.message}</AppAlert>}
                                    <div className="form-group">
                                        <label className="control-label">{t("Nom")} <span className="text-danger">*</span></label>
                                        <input
                                            className="form-control"
                                            value={role.name}
                                            maxLength={100}
                                            required
                                            onChange={(e) => setRole({...role, name: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="control-label">{t("Description")}</label>
                                        <input
                                            className="form-control"
                                            value={role.description || ''}
                                            maxLength={255}
                                            onChange={(e) => setRole({...role, description: e.target.value})}
                                        />
                                    </div>
                                    <label className="control-label font-weight-bold">{t('Modules accessibles')}</label>
                                    {Object.keys(groups).map(group => (
                                        <div key={group} className="mb-3">
                                            <div className="font-weight-bold mb-1">
                                                {t(group)}{' '}
                                                <button type="button" className="btn btn-link btn-sm p-0 ml-2" onClick={() => toggleGroup(groups[group])}>
                                                    {t('Tout cocher / décocher')}
                                                </button>
                                            </div>
                                            <div className="row">
                                                {groups[group].map(module => (
                                                    <div className="col-md-6" key={module.key}>
                                                        <div className="fancy-checkbox">
                                                            <label>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={role.permissions.includes(module.key)}
                                                                    onChange={() => togglePermission(module.key)}
                                                                />
                                                                <span>{t(module.label)}</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-round btn-danger font-weight-bold" disabled={submitting} onClick={() => setRole(null)}>
                                        <span className="fa fa-times"></span>{' '}{t('Annuler')}
                                    </button>
                                    <button type="submit" className="btn btn-round btn-success font-weight-bold" disabled={submitting}>
                                        <span className="fa fa-save"></span>{' '}{submitting ? t('Enregistrement...') : t('Enregistrer')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    authUser: state.auth.user,
});

export default connect(mapStateToProps)(RoleList);
